import { mainApi } from './index';
import type { SocialPlatform } from '@/features/profile/types/profile.types';
import type { ProfileFormValues } from '@/features/profile/validations/profileSchema';
import { constructSocialUrl, getSocialPlatformConfig } from '@/features/profile/constants/socialPlatforms';

export interface UpdateProfileRequest {
    full_name: string;
    bio?: string;
    profile_image?: string;
    instagram?: string;
    tiktok?: string;
    twitter?: string;
    youtube?: string;
    linkedin?: string;
    github?: string;
    discord?: string;
    twitch?: string;
}

export interface UpdateProfileResponse {
    user: {
        id: string;
        full_name: string;
        profile_image?: string;
        data?: {
            bio?: string;
            socials?: Record<string, string>;
        };
    };
}

/**
 * Update user profile information
 *
 * @param data - Profile form values containing display name, bio, and social links
 * @param profileImageUrl - Optional profile image URL
 * @returns Promise resolving to the updated user data
 * @throws Error if the API request fails
 */
export async function updateProfile(data: ProfileFormValues, profileImageUrl?: string): Promise<UpdateProfileResponse> {
    // Build the request payload with social links as individual fields
    const requestData: UpdateProfileRequest = {
        full_name: data.displayName,
        bio: data.bio || undefined,
    };

    // Add profile image if provided
    if (profileImageUrl) {
        requestData.profile_image = profileImageUrl;
    }

    // Add social links as individual fields
    Object.entries(data.socialLinks).forEach(([platform, value]) => {
        if (value && value.trim()) {
            const config = getSocialPlatformConfig(platform as SocialPlatform);

            // For username-based inputs, just pass the username without constructing URL
            if (config && config.inputType === 'username') {
                requestData[platform as keyof UpdateProfileRequest] = value.replace(/^@/, ''); // Remove @ if present
            } else {
                // For URL-based inputs, use the value as-is (or construct if needed)
                const finalUrl = config ? constructSocialUrl(platform as SocialPlatform, value) : value;
                requestData[platform as keyof UpdateProfileRequest] = finalUrl;
            }
        }
    });

    const response = await mainApi.post<UpdateProfileResponse>('v1/users/profile-page-setup', requestData);
    return response.data;
}

/**
 * Update user profile image
 *
 * @param profileImageUrl - URL of the profile image to set
 * @returns Promise resolving to the updated user data
 * @throws Error if the API request fails
 */
export async function updateProfileImage(profileImageUrl: string): Promise<UpdateProfileResponse> {
    const requestData = {
        profile_image: profileImageUrl,
    };

    const response = await mainApi.post<UpdateProfileResponse>('v1/users/profile-page-setup', requestData);
    return response.data;
}

/**
 * Update user social links only (used during subscription flow)
 *
 * @param socials - Object containing social platform keys and their values
 * @returns Promise resolving to the updated user data
 * @throws Error if the API request fails
 */
export async function updateSocialLinks(socials: Record<string, string>): Promise<UpdateProfileResponse> {
    const response = await mainApi.post<UpdateProfileResponse>('v1/users/profile-page-setup', socials);
    return response.data;
}
